import { injectStrict } from '@/injection/injectStrict.ts';
import { ApiKey } from '@/injection/keys.ts';
import { ApiRequestParams } from '@/types/api.type.ts';
import {
    Dealer, DealerBrand, DealerCreateForm, DealerCreateFormErrors, DealerCreateFormErrorsSchema, DealerListItem,
    DealerPaginatorSchema, DealerSchema,
    DealersPaginator, DealersRequestFilter, DealersSchema, DealerUpdateForm, DealerUpdateFormErrors, DealerUpdateFormErrorsSchema,
} from '@/types/dealer.type.ts';
import { User, UsersSchema } from '@/types/user.type.ts';
import {
    LegalEntityUpdateForm,
    LegalEntityUpdateFormErrors,
    LegalEntityUpdateFormErrorsSchema,
} from '@/types/legal-entity.type.ts';

class DeaderRepository
{
    private $Api: any;

    constructor() {
        this.$Api = injectStrict(ApiKey);
    }

    public getDealer(id: number): Promise<Dealer>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('getDealer', requestParams)
                .then(({ data }: any) => {
                    const parsedDealer = DealerSchema.parse(data);
                    const dealer: Dealer = parsedDealer as unknown as Dealer;

                    resolve(dealer);
                });
        });
    }

    public getDealers(dealersRequestFilter?: DealersRequestFilter, oAbortController?: AbortController): Promise<DealersPaginator>
    {
        const requestParams: ApiRequestParams = {
            query: dealersRequestFilter,
        };

        return new Promise((resolve) => {
            this.$Api.callNew('getDealers', requestParams, null, oAbortController)
                .then(({ data }: any) => {
                    const parsedDealersPaginator = DealerPaginatorSchema.parse(data);
                    const dealersPaginator: DealersPaginator = parsedDealersPaginator as unknown as DealersPaginator;

                    resolve(dealersPaginator);
                });
        });
    }

    public createDealer(dealerCreateForm: DealerCreateForm, oAbortController?: AbortController): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            requestBody: dealerCreateForm,
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('createDealer', requestParams, null, oAbortController)
                .then(() => resolve())
                .catch(({ response: { status, data: { errors } } }: any) => {
                    if (status === 422) {
                        const parsedCreateDealerErrors = DealerCreateFormErrorsSchema.parse(errors);
                        const createDealerErrors: DealerCreateFormErrors = parsedCreateDealerErrors as unknown as DealerCreateFormErrors;

                        reject(createDealerErrors);
                    }
                    else {
                        reject();
                    }
                });
        });
    }

    public updateDealer(id: number, dealerUpdateForm: DealerUpdateForm, oAbortController?: AbortController): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
            requestBody: dealerUpdateForm,
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('updateDealer', requestParams, null, oAbortController)
                .then(() => resolve())
                .catch(({ response: { status, data: { errors } } }: any) => {
                    if (status === 422) {
                        const parsedUpdateDealerErrors = DealerUpdateFormErrorsSchema.parse(errors);
                        const updateDealerErrors: DealerUpdateFormErrors = parsedUpdateDealerErrors as unknown as DealerUpdateFormErrors;

                        reject(updateDealerErrors);
                    }
                    else {
                        reject();
                    }
                });
        });
    }

    public activateDealer(id: number): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('activateDealer', requestParams)
                .then(() => resolve());
        });
    }

    public deactivateDealer(id: number): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('deactivateDealer', requestParams)
                .then(() => resolve());
        });
    }

    public getDealerBrands(): Promise<DealerBrand[]>
    {
        return new Promise((resolve) => {
            resolve([
                {
                    name: 'Hyundai',
                    code: 'hyundai',
                },
                {
                    name: 'Genesis',
                    code: 'genesis',
                },
            ]);
        });
    }

    public getDealerUsers(id: number): Promise<User[]>
    {
        return new Promise((resolve) => {
            const requestParams: ApiRequestParams = {
                segments: { id },
            };

            this.$Api.callNew('getDealerUsers', requestParams)
                .then(({ data }: any) => {
                    const parsedUsers = UsersSchema.parse(data);
                    const users = parsedUsers as unknown as User[];

                    resolve(users);
                });
        });
    }

    public getAllActiveDealers(): Promise<DealerListItem[]>
    {
        return new Promise((resolve) => {
            this.$Api.callNew('getAllActiveDealers')
                .then(({ data }: any) => {
                    const parsedDealers = DealersSchema.parse(data);
                    const dealers: DealerListItem[] = parsedDealers as unknown as DealerListItem[];

                    resolve(dealers);
                });
        });
    }

    public updateDealerLegalEntity(id: number, form: LegalEntityUpdateForm): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
            requestBody: form,
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('updateDealerLegalEntity', requestParams)
                .then(() => resolve())
                .catch(({ response: { status, data: { errors } } }: any) => {
                    if (status === 422) {
                        const parsedErrors = LegalEntityUpdateFormErrorsSchema.parse(errors);
                        const updateErrors: LegalEntityUpdateFormErrors = parsedErrors as unknown as LegalEntityUpdateFormErrors;

                        reject(updateErrors);
                    }
                    else {
                        reject();
                    }
                });
        });
    }
}

export default DeaderRepository;
